import heic2any from "heic2any";
import { FileIcon, defaultStyles } from "react-file-icon";
import { toast } from "sonner";

import { cn } from "./utils";

import { ImagePreviewer } from "@/components/ImagePreview/ImagePreviewer";
import { allowedImageTypes } from "@/constants/constants";
import { customToast } from "@/utils/custom-toast";

export const getExtension = (filename: string): string =>
  filename.split(".").pop()?.toLowerCase() || "pdf";

/**
 * @param {string} file - image/ file url
 * @param {string} className - style
 * @param {object} config - config of functions for the previewer
 * @returns {React.JSX.Element} -actual previewer
 */
export function fileImagePreviewer({
  file,
  config,
  className,
  extension,
  size,
}: {
  file: File | string;
  className?: string;
  config?: {
    onClick?: () => void;
  };
  extension?: string;
  size?: number;
}): React.JSX.Element | null {
  const isFileObject = typeof file !== "string";
  const filename = isFileObject ? file.name : file;
  const fileExt = extension ?? getExtension(filename);
  const src = isFileObject ? URL.createObjectURL(file) : file;
  const isImage = allowedImageTypes.includes(fileExt);

  if (isImage) {
    return (
      <div onClick={config?.onClick}>
        <ImagePreviewer
          imagesSrc={src ?? ""}
          alt={src}
          trigger={
            <img
              src={src}
              width={size || 40}
              height={size || 40}
              className={cn("object-cover rounded-lg", className)}
            />
          }
        />
      </div>
    );
  }

  return (
    <div onClick={config?.onClick} className={cn("w-5 h-5", className)}>
      <FileIcon
        extension={fileExt}
        // @ts-expect-error - defaultStyles[fileExt] might not be typed correctly
        {...defaultStyles[fileExt]}
      />
    </div>
  );
}

const compressImage = async (
  blob: Blob,
  targetFormat: "jpeg" | "webp" | "png" = "webp",
  quality = 0.7,
): Promise<Blob> => {
  const imageBitmap = await createImageBitmap(blob);

  const canvas = document.createElement("canvas");
  canvas.width = imageBitmap.width;
  canvas.height = imageBitmap.height;

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Canvas context not available");
  }

  ctx.drawImage(imageBitmap, 0, 0);

  return new Promise<Blob>((resolve, reject) =>
    canvas.toBlob(
      (compressedBlob) => {
        if (!compressedBlob) {
          reject(new Error("Compression failed"));

          return;
        }
        resolve(compressedBlob);
      },
      `image/${targetFormat}`,
      quality,
    ),
  );
};

/**
 * Converts an image file to the specified format.
 * @param {object} params - The parameters for the conversion.
 * @param {File} params.file - The image file to convert.
 * @param {"jpeg" | "png" | "webp"} [params.targetFormat] - The target format for the converted image.
 * @param {number} [params.quality] - The quality of the converted image (increase in quality increases size of file).
 * @returns {Promise<File | null>} A promise that resolves to the converted file or null if conversion fails.
 * @throws {Error} If no file is provided.
 */

export const supportedFormats = [
  "image/heic",
  "image/HEIC",
  "image/heif",
  "image/HEIF",
];
export const convertImageFormat = async ({
  file,
  targetFormat = "jpeg",
  quality = 0.7,
}: {
  file: File;
  targetFormat?: "jpeg" | "png" | "webp";
  quality?: number;
}): Promise<File | null> => {
  if (!file) {
    throw new Error("No file provided");
  }
  if (!supportedFormats.includes(file.type)) {
    return file;
  }
  try {
    const convertedBlob = (await heic2any({
      blob: file,
      toType: `image/${targetFormat}`,
      quality,
    })) as Blob;

    const fileNameWithoutExt = file.name.replace(/\.[^/.]+$/, "");
    const compressedBlob = await compressImage(
      convertedBlob,
      targetFormat,
      quality,
    );

    const convertedFile = new File(
      [compressedBlob],
      `${fileNameWithoutExt}.${targetFormat}`,
      { type: `image/${targetFormat}` },
    );

    return convertedFile;
  } catch (error) {
    customToast.error("Error converting image file");

    return null;
  }
};

export const splitImageFileName = (url: string, imgBaseUrl: string): string => {
  const baseUrl = imgBaseUrl;
  if (!url?.startsWith(baseUrl)) {
    return "";
  }
  // Remove the base URL from the full URL
  const relativeUrl = url?.replace(baseUrl, "");

  // Find the index of the first "-"
  const firstDashIndex = relativeUrl?.indexOf("-");

  // Get the substring after the first "-"
  const fileName = relativeUrl.substring(firstDashIndex + 1);

  return decodeURIComponent(fileName);
};

export const dataUrlToBlob = (base64: string, mimeType: string): Blob => {
  const parts = base64.split(",");
  if (parts.length < 2) {
    toast.info("Invalid data URL format.");
  }
  let [, base64String] = parts;
  base64String = base64String.replace(/\s/g, "");

  let decodedByteString: string;
  try {
    decodedByteString = atob(base64String);
  } catch (error) {
    // toast.error("Failed to process data. Corrupted or invalid data format.");

    return new Blob([], { type: mimeType });
  }

  const arrayBuffer = new ArrayBuffer(decodedByteString.length);
  const uint8Array = new Uint8Array(arrayBuffer);

  for (let i = 0; i < decodedByteString.length; i++) {
    uint8Array[i] = decodedByteString.charCodeAt(i);
  }

  return new Blob([uint8Array], { type: mimeType });
};

//Function to open file preview in new tab
export const openFilePreviewInNewTab = (url: string): void => {
  if (!url) {
    toast.error("No file URL provided");

    return;
  }

  const extension = url.split(".").pop()?.toLowerCase();
  const baseUrl = encodeURIComponent(url);
  const officeViewer = `https://view.officeapps.live.com/op/view.aspx?src=${baseUrl}`;
  const googleViewer = `https://docs.google.com/viewer?url=${baseUrl}`;

  const viewers: Record<string, string> = {
    pdf: googleViewer,
    doc: officeViewer,
    docx: officeViewer,
    xls: officeViewer,
    xlsx: officeViewer,
    ppt: officeViewer,
    pptx: officeViewer,
    csv: googleViewer,
  };

  const viewerUrl = viewers[extension || ""] || url;
  window.open(viewerUrl, "_blank");
};
